/**
 * useShipments - Acesso às remessas do módulo Logística.
 *
 * 100% via RPCs SECURITY DEFINER (get_shipments_paginated, get_shipment_details,
 * upsert_shipment, change_shipment_status, create_shipment_return, delete_shipment,
 * set_shipment_link). Nenhum SELECT direto em tabelas.
 *
 * RELEASE: 2026-10-16 v1.6.0 - invalidate() também recarrega a linha do tempo (sem F5).
 * RELEASE: 2026-10-16 v1.1.0 - Fase 3: itens (adicionar/remover), candidatos de estoque,
 *          endereços Casa do cliente e p_location_id na entrega.
 * RELEASE: 2026-10-16 v1.0.0 - Versão inicial (Fase 2).
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { ShipmentStatus } from '@/components/logistics/shipmentStatus';

const logger = createLogger('useShipments');

// Mantém o `this` do cliente ao chamar RPCs fora da tipagem gerada
const rpc = (name: string, args: Record<string, unknown>) =>
  (supabase.rpc as unknown as (n: string, a: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>)
    .call(supabase, name, args);

export type ShipmentEntityType = 'service_order' | 'deal' | 'task' | 'customer' | 'vehicle';

export interface ShipmentRow {
  id: string;
  number: string;
  direction: 'outbound' | 'return';
  status: ShipmentStatus;
  carrier_person_id: string | null;
  carrier_name: string | null;
  carrier_service: string | null;
  recipient_name: string;
  city: string | null;
  state: string | null;
  tracking_code: string | null;
  expected_delivery_date: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  is_overdue: boolean;
  created_at: string;
  /** get_shipments_paginated v1.1.0: tipo do destinatário */
  recipient_customer_id?: string | null;
  recipient_roles?: string[];
  /** v1.2.0 */
  freight_cost?: number | null;
  items_count?: number;
}

export interface ShipmentDetails {
  id: string;
  number: string;
  direction: 'outbound' | 'return';
  status: ShipmentStatus;
  carrier_person_id: string | null;
  carrier_name: string | null;
  carrier_service: string | null;
  recipient_customer_id: string | null;
  recipient_person_id: string | null;
  recipient_name: string;
  address: Record<string, string | null>;
  tracking_code: string | null;
  freight_cost: number | null;
  expected_delivery_date: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  return_of_id: string | null;
  return_of_number: string | null;
  responsible_user_id: string | null;
  notes: string | null;
  created_at: string;
  recipient_is_provider: boolean;
  /** v1.3.0: papéis ativos da pessoa destinatária */
  recipient_roles?: string[];
  has_serial_items: boolean;
  items: { id: string; item_type: string; item_id: string | null; product_name: string | null; description: string | null; quantity: number; label: string | null; place: { type: string; label: string | null } | null; status?: string | null; components?: ShipmentKitComponent[] | null }[];
  links: { entity_type: ShipmentEntityType; entity_id: string; label?: string | null }[];
  events: { id: string; event_type: string; from_status: string | null; to_status: string | null; message: string | null; created_at: string }[];
}

export interface ShipmentsFilters {
  search: string;
  status: ShipmentStatus | null;
  carrierId: string | null;
  overdueOnly: boolean;
  entityType?: ShipmentEntityType | null;
  entityId?: string | null;
  page: number;
  pageSize: number;
  /** v1.2.0: Envio/Retorno e ordenação (allowlist no servidor) */
  direction?: 'outbound' | 'return' | null;
  sortField?: string | null;
  sortDirection?: 'asc' | 'desc' | null;
  /** v1.3.0: período (allowlist no servidor: created_at|shipped_at|delivered_at|expected) */
  dateField?: 'created_at' | 'shipped_at' | 'delivered_at' | 'expected' | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

export interface ShipmentsPage {
  total: number;
  overdue: number;
  counts: Partial<Record<ShipmentStatus, number>>;
  items: ShipmentRow[];
}

/** v1.3.0: parâmetros de período no fuso do navegador. */
function dateArgs(f: Pick<ShipmentsFilters, 'dateField' | 'dateFrom' | 'dateTo'>) {
  return {
    p_date_field: f.dateField ?? null, p_date_from: f.dateFrom ?? null, p_date_to: f.dateTo ?? null,
    p_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo',
  };
}

/** Filtros do Kanban (sem situação/paginação: cada coluna define a sua). */
export type ShipmentKanbanFilters = Pick<ShipmentsFilters, 'search' | 'carrierId' | 'overdueOnly' | 'direction' | 'dateField' | 'dateFrom' | 'dateTo'>;

const KANBAN_PAGE = 20;

/** Coluna do Kanban de Remessas: paginação infinita reaproveitando get_shipments_paginated. */
export function useShipmentKanbanColumn(status: ShipmentStatus, filters: ShipmentKanbanFilters) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const q = useInfiniteQuery({
    queryKey: ['shipments', tenantId, 'kanban-column', status, filters],
    enabled: !!tenantId,
    initialPageParam: 1,
    queryFn: async ({ pageParam }): Promise<ShipmentRow[]> => {
      const { data, error } = await rpc('get_shipments_paginated', {
        p_search: filters.search || null, p_status: status, p_carrier_person_id: filters.carrierId,
        p_overdue_only: filters.overdueOnly, p_page: pageParam, p_page_size: KANBAN_PAGE,
        p_direction: filters.direction ?? null, p_sort_field: 'created_at', p_sort_direction: 'desc',
        ...dateArgs(filters), p_tenant_hint: tenantId ?? null,
      });
      if (error) { logger.error('Erro na coluna do Kanban de remessas', { error: error.message }); throw new Error(error.message); }
      return ((data as { items?: ShipmentRow[] })?.items) ?? [];
    },
    getNextPageParam: (last, pages) => (last.length === KANBAN_PAGE ? pages.length + 1 : undefined),
  });
  return { ...q, shipments: (q.data?.pages ?? []).flat() };
}

/** Totais por coluna (quantidade e frete) em uma única chamada. */
export function useShipmentKanbanCounts(filters: ShipmentKanbanFilters) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: ['shipments', tenantId, 'kanban-counts', filters],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await rpc('get_shipments_paginated', {
        p_search: filters.search || null, p_carrier_person_id: filters.carrierId,
        p_overdue_only: filters.overdueOnly, p_page: 1, p_page_size: 1,
        p_direction: filters.direction ?? null, ...dateArgs(filters), p_tenant_hint: tenantId ?? null,
      });
      if (error) { logger.error('Erro nos totais do Kanban de remessas', { error: error.message }); throw new Error(error.message); }
      const d = (data || {}) as { counts_filtered?: Record<string, number>; freight_by_status?: Record<string, number> };
      return { counts: d.counts_filtered ?? {}, freight: d.freight_by_status ?? {} };
    },
  });
}

/** Lista paginada + contadores por situação. */
export function useShipmentsList(filters: ShipmentsFilters, enabled = true) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: ['shipments', tenantId, filters],
    enabled: !!tenantId && enabled,
    placeholderData: (prev) => prev,
    queryFn: async (): Promise<ShipmentsPage> => {
      const { data, error } = await rpc('get_shipments_paginated', {
        p_search: filters.search || null,
        p_status: filters.status,
        p_carrier_person_id: filters.carrierId,
        p_entity_type: filters.entityType ?? null,
        p_entity_id: filters.entityId ?? null,
        p_overdue_only: filters.overdueOnly,
        p_page: filters.page,
        p_page_size: filters.pageSize,
        p_direction: filters.direction ?? null,
        p_sort_field: filters.sortField ?? null,
        p_sort_direction: filters.sortDirection ?? null,
        ...dateArgs(filters),
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao listar remessas', { error: error.message });
        throw new Error(error.message);
      }
      const p = (data || {}) as Partial<ShipmentsPage>;
      return { total: p.total ?? 0, overdue: p.overdue ?? 0, counts: p.counts ?? {}, items: p.items ?? [] };
    },
  });
}

/** Detalhes completos (itens, vínculos, linha do tempo). */
export function useShipmentDetails(id: string | null) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: ['shipment', tenantId, id],
    enabled: !!tenantId && !!id,
    queryFn: async () => {
      const { data, error } = await rpc('get_shipment_details', { p_shipment_id: id, p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
      return data as ShipmentDetails;
    },
  });
}

/** Mutações (todas revalidadas no servidor). */
export function useShipmentMutations() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['shipments', tenantId] });
    qc.invalidateQueries({ queryKey: ['shipment', tenantId] });
    // Linha do tempo do modal aberto: eventos novos aparecem sem F5.
    qc.invalidateQueries({ queryKey: ['shipment-timeline'] });
    // A situação da O.S. vinculada muda junto com a remessa (gatilho no servidor): atualiza sem F5.
    qc.invalidateQueries({ queryKey: ['service-orders'] });
    // Aba Movimentações da O.S. lista também o estoque movido pela remessa vinculada
    qc.invalidateQueries({ queryKey: ['service-order-stock'] });
  };
  const onError = (e: Error) => toast.error(e.message);

  const upsert = useMutation({
    mutationFn: async ({ id, data }: { id: string | null; data: Record<string, unknown> }) => {
      const { data: res, error } = await rpc('upsert_shipment', { p_data: data, p_shipment_id: id, p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
      return res as string;
    },
    onSuccess: (_r, v) => { invalidate(); toast.success(v.id ? 'Remessa atualizada' : 'Remessa criada'); },
    onError,
  });

  const changeStatus = useMutation({
    mutationFn: async (v: { id: string; status: ShipmentStatus; deliveredAt?: string | null; message?: string | null; locationId?: string | null }) => {
      const { error } = await rpc('change_shipment_status', {
        p_shipment_id: v.id, p_status: v.status, p_delivered_at: v.deliveredAt ?? null,
        p_message: v.message ?? null, p_location_id: v.locationId ?? null, p_tenant_hint: tenantId ?? null,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => { invalidate(); toast.success('Situação atualizada'); },
    onError,
  });

  const createReturn = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await rpc('create_shipment_return', { p_shipment_id: id, p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
      return data as string;
    },
    onSuccess: () => { invalidate(); toast.success('Remessa de retorno criada'); },
    onError,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await rpc('delete_shipment', { p_shipment_id: id, p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => { invalidate(); toast.success('Rascunho apagado'); },
    onError,
  });

  /** Fase 3: inclui item serializado (tracker/chip/kit) ou livre. */
  const addItem = useMutation({
    mutationFn: async (v: { shipmentId: string; type: 'tracker' | 'chip' | 'kit' | 'free' | 'product'; itemId?: string | null; description?: string; quantity?: number; /** Sem aviso (inclusão em lote na criação). */ silent?: boolean }) => {
      const { error } = await rpc('add_shipment_item', {
        p_shipment_id: v.shipmentId, p_item_type: v.type, p_item_id: v.itemId ?? null,
        p_description: v.description ?? null, p_quantity: v.quantity ?? 1, p_tenant_hint: tenantId ?? null,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: (_r, v) => { invalidate(); qc.invalidateQueries({ queryKey: ['shipment-candidates', tenantId] }); if (!v.silent) toast.success('Item incluído'); },
    onError,
  });

  const removeItem = useMutation({
    mutationFn: async (itemId: string) => {
      const { error } = await rpc('remove_shipment_item', { p_item_id: itemId, p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => { invalidate(); qc.invalidateQueries({ queryKey: ['shipment-candidates', tenantId] }); toast.success('Item removido'); },
    onError,
  });

  /** v1.2.0: inclui/remove vínculo (O.S., Negócio, Tarefa, Cliente, Veículo) via set_shipment_link. */
  const setLink = useMutation({
    mutationFn: async (v: { shipmentId: string; entityType: ShipmentEntityType; entityId: string; linked: boolean }) => {
      const { error } = await rpc('set_shipment_link', {
        p_shipment_id: v.shipmentId, p_entity_type: v.entityType, p_entity_id: v.entityId,
        p_linked: v.linked, p_tenant_hint: tenantId ?? null,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => invalidate(),
    onError,
  });

  return { upsert, changeStatus, createReturn, remove, addItem, removeItem, setLink };
}

export interface ShipmentStockCandidate {
  type: 'tracker' | 'chip' | 'kit';
  id: string;
  label: string;
  extra: string | null;
  place: { type: string; label: string | null } | null;
  /** v1.3.0: situação do item e composição do kit. */
  status?: string | null;
  components?: ShipmentKitComponent[] | null;
}

/** Componente do kit (rastreador/chip) com identificação e situação. */
export interface ShipmentKitComponent { type: 'tracker' | 'chip'; label: string | null; status: string | null }

/** Dados sempre frescos: o modal não pode mostrar lista em cache (sem F5). */
const FRESH = { staleTime: 0, refetchOnMount: 'always' as const, refetchOnWindowFocus: true };

/** Filtro de local da pesquisa de equipamentos. */
export type ShipmentCandidatePlace = 'location' | 'provider' | 'none' | null;
const CANDIDATES_PAGE = 30;

/**
 * Itens de estoque disponíveis (ativos, fora de kit, não instalados, sem remessa aberta).
 * v1.1.0: `excludeProviderId` oculta itens já no estoque do prestador destinatário.
 * v1.2.0: paginação infinita (páginas de 30) e filtro por local.
 */
export function useShipmentStockCandidates(
  kind: 'tracker' | 'chip' | 'kit',
  search: string,
  enabled: boolean,
  excludeProviderId: string | null = null,
  placeType: ShipmentCandidatePlace = null,
) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  return useInfiniteQuery({
    queryKey: ['shipment-candidates', tenantId, kind, search, excludeProviderId, placeType],
    enabled: !!tenantId && enabled,
    ...FRESH,
    initialPageParam: 0,
    getNextPageParam: (last: ShipmentStockCandidate[], all) => (last.length < CANDIDATES_PAGE ? undefined : all.length * CANDIDATES_PAGE),
    queryFn: async ({ pageParam }) => {
      const { data, error } = await rpc('get_shipment_stock_candidates', {
        p_kind: kind, p_search: search || null, p_exclude_provider_person_id: excludeProviderId,
        p_place_type: placeType, p_offset: pageParam, p_tenant_hint: tenantId ?? null,
      });
      if (error) throw new Error(error.message);
      return (data ?? []) as ShipmentStockCandidate[];
    },
  });
}

/** Fase 3: endereços de estoque do tipo Casa do cliente (entrega a cliente). */
export function useShipmentDeliveryLocations(enabled: boolean) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: ['shipment-delivery-locations', tenantId],
    enabled: !!tenantId && enabled,
    ...FRESH,
    queryFn: async () => {
      const { data, error } = await rpc('get_shipment_delivery_locations', { p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
      return (data ?? []) as { id: string; name: string }[];
    },
  });
}

/** Produto candidato a item da remessa (get_shipment_product_candidates v1.0.0). */
export interface ShipmentProductCandidate {
  id: string;
  name: string;
  sku: string | null;
  product_type: string | null;
  unit_type: string | null;
  /** Quantidade sugerida (do negócio) ou 1 no catálogo. */
  quantity: number;
}

/**
 * Produtos para a remessa: com `dealId` lista os produtos do negócio; sem ele, catálogo
 * ativo do submenu Produtos (páginas de 30). Sempre fresco ao abrir.
 */
export function useShipmentProductCandidates(search: string, dealId: string | null, enabled: boolean) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  return useInfiniteQuery({
    queryKey: ['shipment-candidates', tenantId, 'product', search, dealId],
    enabled: !!tenantId && enabled,
    ...FRESH,
    initialPageParam: 0,
    getNextPageParam: (last: { items: ShipmentProductCandidate[]; has_more: boolean }, all) =>
      (last.has_more ? all.reduce((n, p) => n + p.items.length, 0) : undefined),
    queryFn: async ({ pageParam }) => {
      const { data, error } = await rpc('get_shipment_product_candidates', {
        p_search: search || null, p_deal_id: dealId, p_offset: pageParam, p_tenant_hint: tenantId ?? null,
      });
      if (error) throw new Error(error.message);
      return (data ?? { items: [], has_more: false }) as { items: ShipmentProductCandidate[]; has_more: boolean };
    },
  });
}
